/**
 * pruefidNames — resolves Prüfidentifikator → Prozessname.
 *
 * Source is the "Prüf-ID Prozessschritt" sheet of the Prüfidentifikatoren-Excel
 * (mirrored as JSON under /pruefids/<release>/<source>/01_*.json by
 * build-data.ts; index in artifacts.json `pruefidSets`). A pruefid appears in
 * several process-step rows — we take the first non-empty `beschreibung_de`
 * (the short use-case name from the AHB).
 *
 * Results are cached per release; a missing release/sheet resolves to an empty
 * map so callers can render plain pruefid numbers without special-casing.
 */
import artifactsData from "@/data/generated/artifacts.json";
import type { ArtifactIndex, PruefidSet, PruefidTableDoc } from "@/types";

const cache = new Map<string, Promise<Map<string, string>>>();

function pickRelease(release: string): { release: string; set: PruefidSet } | null {
  const sets = (artifactsData as unknown as ArtifactIndex).pruefidSets ?? {};
  const releases = Object.keys(sets).sort();
  // exact match first, otherwise the newest release at or below the requested
  // one (Prüfid process names are stable enough for a display label).
  const candidates = [release, ...releases.filter((r) => r <= release).reverse(), ...releases.reverse()];
  for (const r of candidates) {
    const list = sets[r];
    if (list && list.length > 0) return { release: r, set: list[0] };
  }
  return null;
}

async function load(release: string): Promise<Map<string, string>> {
  const picked = pickRelease(release);
  if (!picked) return new Map();
  const table = picked.set.tables.find(
    (t) => t.file.startsWith("01_") || /prozessschritt/i.test(t.sheet),
  );
  if (!table) return new Map();
  const url = `/pruefids/${encodeURIComponent(picked.release)}/${encodeURIComponent(picked.set.source)}/${encodeURIComponent(table.file)}`;
  const res = await fetch(url);
  if (!res.ok) return new Map();
  const doc = (await res.json()) as PruefidTableDoc;
  const map = new Map<string, string>();
  for (const row of doc.rows ?? []) {
    const id = row["pruefidentifika"];
    const name = row["beschreibung_de"];
    if (id == null || name == null) continue;
    const key = String(id);
    if (!map.has(key)) map.set(key, String(name));
  }
  return map;
}

/** Cached pruefid→Prozessname map for a release. Never rejects. */
export function getPruefidNames(release: string): Promise<Map<string, string>> {
  let p = cache.get(release);
  if (!p) {
    p = load(release).catch(() => new Map<string, string>());
    cache.set(release, p);
  }
  return p;
}
